import { calcTotals, normalizeLidarBlocks, cleanGroupLabel, quoteLineGroups, lineGroupNotesFrom } from "./builderHelpers.js";
import { initSections, defaultFollowUp } from "./utils.js";
import { leanAttachments } from "./draftRecord.js";
import { quoteEquipmentIds, normalizeQuoteCrew } from "./holds.js";
import { normalizeBilling } from "./proBono.js";

/* The one stored shape of a quote line, for every save path: Ingest, Save Draft and
   draft preservation. Save Draft and preservation used to write their own copy that
   left out `group`, so Save Draft quietly took a line out of its block. Drops the
   builder-only keys (`key`, `isCustomRate`, `savedToDb`). */
export function serializeLineItem(li) {
  return {
    catalogId: li.catalogId || null,
    description: li.description || "",
    notes: li.notes || "",
    qty: li.qty,
    rate: Number(li.rate) || 0,
    discount: li.discount || null,
    // The line's group (PDF heading + subtotal), only when it has one.
    ...(cleanGroupLabel(li.group) ? { group: cleanGroupLabel(li.group) } : {}),
    ...(li.lidar ? { lidar: li.lidar } : {}),
    ...(li.drone ? { drone: li.drone } : {}),
  };
}

/* Saved Quotes decorates its rows with `_bucket`, `_linkedJob` and others for the
   list, and Edit hands that row to the loader, so they reached Firestore: 11 of 164
   quotes carried them on 2026-11-02. Builder-only `_` keys are dropped the same way.
   `_docId` stays: the data layer stamps it and other code reads it. Existing documents
   are not cleaned here; a merge save leaves their old keys, and every load drops them. */
export function stripListRowKeys(q) {
  if (!q || typeof q !== "object") return q;
  const out = {};
  for (const [k, v] of Object.entries(q)) {
    if (k.startsWith("_") && k !== "_docId") continue;
    out[k] = v;
  }
  return out;
}

/* The record Save Draft and draft preservation write: the whole quote (drafts keep
   everything), with the shared line shape, lean attachments and totals recomputed from
   the live lines. `draft` says whether it files as a draft or stays an active quote. */
export function buildDraftSaveRecord(q, { draft = true, now = new Date().toISOString() } = {}) {
  const freshTotals = calcTotals(q.lineItems || [], q.quoteDiscounts || []);
  return {
    ...stripListRowKeys(q),
    draft,
    savedAt: now,
    lineItems: (q.lineItems || []).map(serializeLineItem),
    attachments: leanAttachments(q.attachments),
    subtotal: freshTotals.subtotal,
    totalSavings: freshTotals.totalSavings,
    total: freshTotals.total,
  };
}

/* ═══ FIELDS THE LISTS OWN ═══
   Mark lost, Archive, Restore to Open, the follow-up buttons and Trash write these on
   the stored record while the same quote may be open in the builder. A save from the
   builder writes the builder's value only where the builder changed it since it loaded
   the quote; otherwise the stored value stands, so saving a parked quote can't undo a
   list action. `baseline` is what the builder loaded. */
export const LIST_OWNED_FIELDS = ["jobStatus", "followUpDate", "followUpHistory", "deletedAt"];

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

export function withListOwnedFields(record, { q, baseline, stored }) {
  if (!stored || !baseline) return record;
  const out = { ...record };
  for (const f of LIST_OWNED_FIELDS) {
    const edited = !same(q?.[f], baseline[f]);
    if (edited) continue;
    if (Object.prototype.hasOwnProperty.call(stored, f)) out[f] = stored[f];
    else delete out[f];
  }
  return out;
}

/** The stored files a save leaves behind: attachments the previous record had under this
 *  quote's own folder that the new record no longer lists. A duplicate shares its source's
 *  files (another quote's folder), and those are never this quote's to delete. */
export function removedAttachmentPaths(prevSaved, record) {
  const quoteId = record?.id;
  if (!quoteId) return [];
  const keep = new Set((record.attachments || []).map((a) => a?.storagePath).filter(Boolean));
  const own = `quote-attachments/${quoteId}/`;
  return (prevSaved?.attachments || [])
    .map((a) => a?.storagePath)
    .filter((p) => p && p.startsWith(own) && !keep.has(p));
}

/* ═══ THE INGEST SAVE RECORD ═══
   The whitelist that turns the live builder quote into the Firestore document
   dataService.quotes.save() writes on Ingest. Moved out of DataContext.saveQuote
   verbatim (2026-09-03) so it can be unit-tested: the GO review found nothing
   pinned that a field like lidarPrefill survives the save. Drafts use a different
   path (draftRecord.js spreads the whole quote); this is the ingested shape.

   Everything the caller resolved with side effects comes in through `ctx`:
   the previously saved doc (createdAt survives), the CRM commit result, the
   client lookup, and the clock. */
export function buildQuoteSaveRecord(q, {
  prevSaved = null,
  quoteDate,
  commit = {},
  matchedClient = null,
  now = new Date().toISOString(),
  // The live equipment id-to-name map (lib/equipment.js getEqNames()). Required
  // whenever the quote carries equipment: without it a display name would be
  // persisted as an id, so the save refuses instead (the one caller,
  // DataContext.saveQuote, always passes it).
  equipmentMap = null,
} = {}) {
  // The effective equipment (the stored ids, or the legacy display names on a record
  // that predates equipmentIds), read through the one reader the engine uses.
  const effectiveEquipment = quoteEquipmentIds(q, equipmentMap);
  if (!equipmentMap && effectiveEquipment.length) {
    throw new Error("buildQuoteSaveRecord: equipmentMap is required to persist equipmentIds (pass getEqNames()).");
  }
  const freshTotals = calcTotals(q.lineItems || [], q.quoteDiscounts || []);
  const qd = quoteDate || q.quoteDate || now.split("T")[0];
  // The block list, derived from the legacy fields when the quote predates it, so every
  // saved quote carries one from here on; lineGroupNotes follows it and can't disagree.
  const lineGroups = quoteLineGroups(q);
  return {
    ...stripListRowKeys(prevSaved || {}),
    ...stripListRowKeys(q),
    id: q.id,
    draft: false,
    docType: q.docType || "quote",
    clientName: q.clientName || "",
    contactName: q.contactName || "",
    contactEmail: q.contactEmail || "",
    contactPhone: q.contactPhone || "",
    productionName: q.productionName || "",
    sourceProspectId: q.sourceProspectId || null,
    // An id the operator explicitly picked wins; then the person the contact-match
    // commit just created or linked. No name/email lookup (2026-10-26): a failed
    // create must leave the quote unlinked, not linked to whoever a stale email
    // or a same-name record points at, so saveQuote can refuse it.
    contactEntityId: q.contactEntityId || commit.contactEntityId || null,
    companyEntityId: q.companyEntityId || commit.companyEntityId || matchedClient?.id || q.clientId || null,
    jobLocation: q.jobLocation || "",
    isStudio: !!q.isStudio,
    scanDate: q.scanDate || "",
    scanDateTBD: !!q.scanDateTBD,
    multiDay: !!q.multiDay,
    sessions: Array.isArray(q.sessions) ? q.sessions : [],
    // Holds (2026-09-04): canonical unit ids, the roster-keyed crew list, and the
    // switch, normalized so the record never carries display names or loose shapes.
    equipmentIds: effectiveEquipment,
    crew: normalizeQuoteCrew(q.crew),
    holdOff: !!q.holdOff,
    // Pro bono (2026-09-11): the flag rides with the discount that makes the
    // total $0. Always written ("standard" when off) so switching a saved quote
    // back to standard clears the stored flag. The GO review caught this
    // whitelist dropping it: the discount persisted, the flag did not.
    billing: normalizeBilling(q.billing),
    summary: q.summary || "",
    turnaround: q.turnaround || "",
    turnaroundTBD: !!q.turnaroundTBD,
    turnaroundUnit: q.turnaroundUnit || null,
    lidarDetected: !!q.lidarDetected,
    // Facts the AI read for the LiDAR estimator, one block per scope; re-normalized so
    // only the sanctioned shape (a list) is ever persisted. null when nothing was stated.
    lidarPrefill: normalizeLidarBlocks(q.lidarPrefill) || null,
    // The blocks in print order, and the heading notes per block derived from them.
    lineGroups,
    lineGroupNotes: lineGroupNotesFrom(lineGroups),
    deliverableText: q.deliverableText || "",
    deliverableFormats: q.deliverableFormats || ["obj"],
    requiresDeposit: !!q.requiresDeposit,
    // Structured deposit (2026-08-28). Persist only a real shape; null lets
    // migrateQuote re-stamp the legacy-derived form at next load.
    deposit: (q.deposit && typeof q.deposit === "object" && q.deposit.type) ? q.deposit : null,
    // Tri-state: null = auto (F5), true/false = explicit override. `!!` would
    // collapse auto into a hard `false` and silently drop the pricing sheet
    // from saved quotes — preserve null.
    includePricingSheet: q.includePricingSheet == null ? null : !!q.includePricingSheet,
    industry: q.industry || "other",
    leadSource: q.leadSource || "referral",
    referredBy: q.referredBy || "",
    jobStatus: q.jobStatus || "quoted",
    quoteDate: qd,
    followUpDate: q.followUpDate || defaultFollowUp(qd),
    internalNotes: q.internalNotes || "",
    lineItems: (q.lineItems || []).map(serializeLineItem),
    quoteDiscounts: (q.quoteDiscounts || []).map(disc => ({
      name: disc.name || "",
      type: disc.type || "%",
      value: Number(disc.value) || 0,
      reason: disc.reason || "",
    })),
    total: freshTotals.total,
    mathVerified: true,
    subtotal: freshTotals.subtotal,
    totalSavings: freshTotals.totalSavings,
    sections: q.sections || initSections(),
    emailText: q.emailText || "",
    notesText: q.notesText || "",
    // Who wrote the notes (2026-10-09): only "operator" notes can carry an AI Start
    // discount line (aiStartDiscount.js notesAreOperator). Saved with the notes.
    notesSource: q.notesSource || "",
    attachments: leanAttachments(q.attachments),
    versions: q.versions || [],
    changeOrderOfJobId: q.changeOrderOfJobId || null,
    changeOrderOfQuoteId: q.changeOrderOfQuoteId || null,
    createdAt: prevSaved?.createdAt || q.createdAt || now,
    savedAt: now,
  };
}
